<!-- 這是逐個file upload的uploader -->
<template>
  <v-row>
    <v-file-input
      v-show="false"
      v-model="fileUploaded"
      :ref="inputRef"
      :accept="acceptType"
      @change="_onFilePicked($event)"
      :disabled="isDisabled"
    />
    <v-text-field 
      v-show="false"
      :rules="[v => required ? $validate.required(v) : true]"
      :value="fileValue"
      ref="checkInput"
    />

    <v-col v-if="noTextUploadBtn" cols="3">
      <v-btn
        depressed
        @click="_triggerUploadFile()"
        :disabled="isDisabled"
        :loading="uploading"
        class="no-text-upload-btn"
      >
        <v-icon color="#5e566924">{{ icon.mdiPlus }}</v-icon>
      </v-btn>
    </v-col>
    <v-col :cols="noTextUploadBtn ? '9' : '12'">
      <div class="image-upload-display">
        <div v-if="!multiple && fileValue">
          <v-img
            class="preview-img"
            :src="getFile(fileValue)"
            :max-height="150"
            :max-width="250"
            @click="_previewImg()"
          />
          <div class="d-flex justify-space-between mt-1">
            <v-btn depressed small text color="error" @click="_removeImg()" :disabled="isDisabled">{{ $t('delete') }}</v-btn>
          </div>
        </div>
        <template v-else-if="multiple && fileValue.length > 0">
          <div v-for="(file, index) of fileValue" :key="index">
            <v-img
              class="preview-img"
              :src="getFile(file)"
              :max-height="150"
              :max-width="250"
              @click="_previewImg(index)"
            ></v-img>
            <div class="d-flex justify-space-between mt-1">
              <v-btn depressed small text color="error" @click="_removeImg(index)" :disabled="isDisabled">{{ $t('delete') }}</v-btn>
            </div>
          </div>
        </template>
        <v-img 
          v-else
          :src="require('@/assets/images/empty-image.svg')"
          :max-height="150"
          :max-width="250"
        />
      </div>
    </v-col>
    <v-col cols="12" v-if="!noTextUploadBtn">
      <v-btn
        color="primary"
        depressed
        @click="_triggerUploadFile()"
        :disabled="isDisabled"
        :loading="uploading"
      >{{ $t(label) }}</v-btn>
    </v-col>
    <v-col class="py-0 mb-2">
      <span class="text-caption error--text">{{ $t(errorText) }}</span>
    </v-col>
  </v-row>
</template>

<script>
import { mdiPlus } from '@mdi/js'

export default {
  name: 'ImageUploader',
  props: {
    inputRef: {
      type: String,
      default: 'upload_file',
      required: true,
    },
    label: {
      type: String,
      default: 'upload',
    },
    fileValue: {
      type: String | Array,
      default: '',
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    fileType: {
      type: String,
      default: 'image', // 'image' | 'video' | 'imgWithPDF' | 'doc'
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    max: {
      type: Number,
      default: -1,
    },
    noTextUploadBtn: {
      type: Boolean,
      default: false,
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isDisabled() {
      return this.$store.getters.isUploading || this.disabled || this.$store.getters.isLoading
    },
    uploading() {
      return this.$store.getters.isUploading
    },
    acceptType() {
      return this.$common.getAcceptFileFormat(this.fileType)
    },
  },
  setup() {
    return {
      icon: {
        mdiPlus
      },
    }
  },
  data: () => ({
    fileUploaded: null,
    errorText: ''
  }),
  methods: {
    getFile(file) {
      if (file.indexOf('pdf') !== -1) {
        return require('@/assets/images/file-pdf-box.svg')
      } else if (file.indexOf('mp4') !== -1 || file.indexOf('webm') !== -1) {
        return require('@/assets/images/video.svg')
      }

      return this.$mediaPath + file
    },
    _previewImg(index = 0) {
      let list = [];
      if (this.multiple) {
        list = this.fileValue;
      } else if (this.fileValue) {
        list.push(this.fileValue);
      }

      if (this.$validate.DataValid(list[index])) {
        window.open(this.$mediaPath + list[index])
      }

      this.$store.dispatch('toggleSnack', {
        message: 'message.fileNotExist',
        type: 'error',
      })
    },
    _removeImg(index = 0) {
      this.fileUploaded = null
      if (this.multiple) {
        const fileList = this.$common.duplicateData(this.fileValue)
        fileList.splice(index, 1)
        this.$emit('update:fileValue', fileList)
        if (this.required && fileList.length < 1) {
          this.errorText = 'form.required';
        }
      } else {
        this.$emit('update:fileValue', '')
        if (this.required) {
          this.errorText = 'form.required';
        }
      }

      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },
    _triggerUploadFile() {
      if (this.$store.getters.isLoading || this.$store.getters.uploading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      if (this.max > -1 && this.multiple && this.fileValue.length >= this.max) {
        this.$store.dispatch('toggleSnack', {
          type: 'error',
          message: 'message.reachUploadMax',
        })
        return;
      }

      this.$refs[this.inputRef].$refs.input.click()
    },

    async _onFilePicked(file) {
      if (!file || (Array.isArray(file) && file.length < 1)) {
        if (this.required) {
          this.errorText = 'form.required';
        }
      
        return
      }

      // check max
      if (this.multiple && this.max > -1 && this.fileValue.length + file.length > this.max) {
        this.$store.dispatch('toggleSnack', {
          message: this.$t('message.uploadExceedMax', { rest: this.max - this.fileValue.length}),
          type: 'error',
        });
        return
      }

      const check = await this.$validate.validateFileInput(file, this.acceptType)
      if (check !== true) {
        this.$store.dispatch('toggleSnack', {
          message: check,
          type: 'error',
        });
        return
      }

      this.$store.dispatch('setUploadLoading', true);
      const reader = new FileReader();
      reader.onload = async event => {
        const payload = {
          upload_data: event.target.result,
          upload_file_type: file.type.split('/').pop(),
        }

        this.$XHR.post(
          {
            'upload_file': JSON.stringify(payload)
          },
          progress => {
            this.$store.dispatch('toggleSnack', {
              type: 'processing',
              message: this.$t('message.uploading', { percentage: this.$common.formatUploadProgress(progress.loaded, progress.total) }),
              closeManual: true,
            })
          }
        ).then(data => {
          this.$store.dispatch('setUploadLoading', false)

          if (this.multiple) {
            const fileList = [...this.fileValue];
            fileList.push(data)
            this.$emit('update:fileValue', fileList)
          } else {
            this.$emit('update:fileValue', data)
          }

          if (this.dispatchUpdateOnChange === true) {
            this.$store.dispatch('setDataIsUpdated', true)
          }

          this.$store.dispatch('toggleSnack', {
            message: 'message.uploadSuccess',
            type: 'success',
          })
        }).catch(err => {
          this.$store.dispatch('setUploadLoading', false)
          this.$common.error(err)
          this.$store.dispatch('toggleSnack', {
            message: 'message.uploadFail',
            type: 'error',
          })
        });
      }
      reader.readAsDataURL(file)
    },
  },
  mounted() {
    this.$watch(
      () => this.$refs.checkInput.validationState,
      (val) => {
        if (val === 'error') {
          this.errorText = 'form.required';
        } else {
          this.errorText = '';
        }
      }
    );
  }
}
</script>

<style lang="scss" scoped>
.preview-img {
  &:hover {
    cursor: pointer;
  }
}

.image-upload-display {
  display: flex;
  overflow-x: auto;
  align-items: flex-end;

  & > div:not(:first-child) {
    margin-left: 20px;
  }

  & .img-des {
    white-space: pre-wrap;
    display: -webkit-inline-box;
    -webkit-line-clamp: 2;
    overflow: hidden;
    max-width: 250px;
    -webkit-box-orient: vertical;
  }
}

.no-text-upload-btn.v-btn {
  width: 100% !important;
  height: 150px !important;
  border: 2px solid rgba(94, 86, 105, 0.141);
  min-width: unset !important;
}
</style>
